import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted }) => {
    const { t } = useTranslation();
    return (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? t('common.symbols.emDash')}
    </td>
    );
};

const fmt = (n, emDash) => (n != null ? Number(n).toLocaleString() : emDash);

const formatDate = (d, emDash) => {
    if (!d) return emDash;
    try {
        return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return d;
    }
};

const SalesDailyTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    const strings = t('salesAnalytics.dailyTable', { returnObjects: true });
    const emDash = t('common.symbols.emDash');
    const maxCount = useMemo(() =>
        Math.max(...items.map((r) => r.total_orders ?? r.order_count ?? r.count ?? 0), 1),
        [items]
    );

    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{strings.title}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{strings.subtitle}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', borderRadius: 20, padding: '3px 10px' }}>
                        {strings.daysCount.replace('{count}', items.length)}
                    </span>
                )}
            </div>

            {loading && <EmptyState message={strings.loading} />}
            {!loading && !items.length && <EmptyState message={strings.noData} />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                        <thead>
                            <tr>
                                <Th>{strings.headers.index}</Th>
                                <Th>{strings.headers.date}</Th>
                                <Th>{strings.headers.orders}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => {
                                const count = row.total_orders ?? row.order_count ?? row.count ?? 0;
                                const countPct = (count / maxCount) * 100;

                                return (
                                    <tr
                                        key={row.order_date ?? row.date ?? i}
                                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                    >
                                        <Cell muted>{i + 1}</Cell>
                                        <Cell>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>
                                                {formatDate(row.order_date ?? row.date, emDash)}
                                            </span>
                                        </Cell>
                                        <Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: '#16a34a', minWidth: 28 }}>
                                                    {fmt(count, emDash)}
                                                </span>
                                                <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', minWidth: 60 }}>
                                                    <div style={{
                                                        height: '100%', borderRadius: 3,
                                                        backgroundColor: '#16a34a',
                                                        width: `${countPct}%`,
                                                        transition: 'width 0.4s',
                                                    }} />
                                                </div>
                                            </div>
                                        </Cell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default SalesDailyTable;
